/**
 * 乒乓球游戏入口
 *
 * P1（玩家）在右侧，P2（Bot）在左侧。
 * 当嵌入 pixijs-aibot 的 iframe 时，P2 由宿主通过 postMessage 控制。
 * 独立运行时，P2 使用内置简单 AI。
 */

import Phaser from 'phaser'
import { PingPongScene } from './game/PingPongScene'
import { GameBridge } from './platform/Bridge'
import { initPingPongSidePanel } from './ui/PingPongSidePanel'

// 全局 bridge 实例，场景通过 registry 获取
const bridge = new GameBridge()

const START_BGM_URL = '/assets/audio/start-bgm.mp3?v=20260507'
const GAME_BGM_URL = '/assets/audio/game-bgm.mp3?v=20260507'
const START_BUTTON_SFX_URL = '/assets/audio/start-button.mp3?v=20260507'
const START_BGM_VOLUME = 0.08
const GAME_BGM_VOLUME = 0.08
const GAME_BGM_DUCK_VOLUME = 0.035
const START_BUTTON_SFX_VOLUME = 0.78

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  parent: 'game-container',
  width: 800,
  height: 600,
  backgroundColor: '#0a0a1a',
  antialias: true,
  antialiasGL: true,
  roundPixels: true,
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
  },
  scene: [PingPongScene],
  callbacks: {
    preBoot: (game) => {
      // 通过 registry 传递 bridge 给场景
      game.registry.set('bridge', bridge)
    },
  },
}

const startOverlay = document.getElementById('start-overlay')
const startButton = document.getElementById('start-button') as HTMLButtonElement | null
const startVideo = document.getElementById('start-video') as HTMLVideoElement | null
let arenaReady = false
let waitingForArena = false

const startBgm = new Audio(START_BGM_URL)
const gameBgm = new Audio(GAME_BGM_URL)
const startButtonSfx = new Audio(START_BUTTON_SFX_URL)
const fadeFrames = new WeakMap<HTMLMediaElement, number>()
let startAudioUnlocked = false
let returnHitDuckTimer: number | null = null

startBgm.loop = true
startBgm.preload = 'auto'
startBgm.volume = 0
gameBgm.loop = true
gameBgm.preload = 'auto'
gameBgm.volume = 0
startButtonSfx.preload = 'auto'
startButtonSfx.volume = START_BUTTON_SFX_VOLUME

function clampVolume(value: number): number {
  return Math.max(0, Math.min(value, 1))
}

function fadeAudio(audio: HTMLMediaElement, targetVolume: number, durationMs: number, onComplete?: () => void): void {
  const activeFrame = fadeFrames.get(audio)
  if (activeFrame !== undefined) {
    cancelAnimationFrame(activeFrame)
  }

  const from = audio.volume
  const target = clampVolume(targetVolume)
  const duration = Math.max(durationMs, 1)
  const startedAt = performance.now()

  const tick = (time: number): void => {
    const t = Math.min((time - startedAt) / duration, 1)
    const eased = 1 - Math.pow(1 - t, 3)
    audio.volume = clampVolume(Phaser.Math.Linear(from, target, eased))

    if (t < 1) {
      fadeFrames.set(audio, requestAnimationFrame(tick))
      return
    }

    fadeFrames.delete(audio)
    onComplete?.()
  }

  fadeFrames.set(audio, requestAnimationFrame(tick))
}

function playLoopingAudio(audio: HTMLMediaElement, targetVolume: number, fadeMs: number): Promise<boolean> {
  if (!audio.paused) {
    fadeAudio(audio, targetVolume, fadeMs)
    return Promise.resolve(true)
  }

  audio.volume = 0
  return audio.play()
    .then(() => fadeAudio(audio, targetVolume, fadeMs))
    .then(() => true)
    .catch(() => {
      // Browsers may block audio until a user gesture; the unlock handler retries.
      return false
    })
}

function fadeOutAndPause(audio: HTMLMediaElement, durationMs: number): void {
  fadeAudio(audio, 0, durationMs, () => {
    audio.pause()
  })
}

function playStartBgm(): Promise<boolean> {
  if (startOverlay?.classList.contains('is-hidden')) return Promise.resolve(false)
  return playLoopingAudio(startBgm, START_BGM_VOLUME, 900)
}

function playGameBgm(): void {
  void playLoopingAudio(gameBgm, GAME_BGM_VOLUME, 900)
}

function duckGameBgm(): void {
  if (gameBgm.paused) return
  fadeAudio(gameBgm, GAME_BGM_DUCK_VOLUME, 180)
}

function restoreGameBgm(): void {
  if (gameBgm.paused) return
  fadeAudio(gameBgm, GAME_BGM_VOLUME, 520)
}

function duckGameBgmForReturnHit(event: Event): void {
  if (gameBgm.paused) return
  const tier = Math.max(1, Math.min((event as CustomEvent<{ tier?: number }>).detail?.tier ?? 1, 4))
  fadeAudio(gameBgm, Math.max(0.018, GAME_BGM_VOLUME - 0.018 - tier * 0.006), 45)
  if (returnHitDuckTimer !== null) {
    window.clearTimeout(returnHitDuckTimer)
  }
  returnHitDuckTimer = window.setTimeout(() => {
    returnHitDuckTimer = null
    restoreGameBgm()
  }, 115 + tier * 18)
}

function playStartButtonSfx(): void {
  startButtonSfx.currentTime = 0
  startButtonSfx.volume = START_BUTTON_SFX_VOLUME
  startButtonSfx.play().catch(() => {})
}

function unlockStartAudio(): void {
  void playStartBgm().then((played) => {
    if (!played) return
    startAudioUnlocked = true
    window.removeEventListener('pointerdown', unlockStartAudio, true)
    window.removeEventListener('pointerup', unlockStartAudio, true)
    window.removeEventListener('click', unlockStartAudio, true)
    window.removeEventListener('keydown', unlockStartAudio, true)
  })
}

function enterGame(): void {
  waitingForArena = false
  startAudioUnlocked = true
  window.removeEventListener('pointerdown', unlockStartAudio, true)
  window.removeEventListener('pointerup', unlockStartAudio, true)
  window.removeEventListener('click', unlockStartAudio, true)
  window.removeEventListener('keydown', unlockStartAudio, true)
  startButton?.classList.remove('is-loading')
  startButton?.removeAttribute('aria-busy')
  startOverlay?.classList.add('is-hidden')
  fadeOutAndPause(startBgm, 420)
  playGameBgm()
  startVideo?.play().catch(() => {})
  window.dispatchEvent(new CustomEvent('ping-pong:start'))
}

window.addEventListener('ping-pong:arena-ready', () => {
  arenaReady = true

  if (waitingForArena) {
    enterGame()
  }
})

// 立即注册音频解锁监听，让用户在 Loading 阶段任何点击都能给浏览器一个 user gesture，
// 这样预加载完成、开始页一出现，BGM 就能立刻响起，而不是要等用户先点击开始按钮才有声音。
window.addEventListener('pointerdown', unlockStartAudio, true)
window.addEventListener('pointerup', unlockStartAudio, true)
window.addEventListener('click', unlockStartAudio, true)
window.addEventListener('keydown', unlockStartAudio, true)

// ===== 预加载所有关键资源 =====

interface PreloadAsset {
  url: string
  bytes: number // 估算文件大小，用于计算总进度权重
  label?: string
  skillId?: string // 技能视频专用：用于注册到技能视频缓存
  mediaId?: string // 通用视频/媒体预热标识（开始页视频等）
}

const PRELOAD_ASSETS: PreloadAsset[] = [
  // Loading 阶段完整加载关键资源：开始页视频、技能 cut-in 视频、BGM/音效、字体和战斗素材。
  // 这样进入开始界面时视频已经可播，首次释放技能也不会再临场拉取大视频。
  { url: START_BGM_URL, bytes: 1_420_000, label: '音频资源' },
  { url: GAME_BGM_URL, bytes: 2_260_000 },
  { url: START_BUTTON_SFX_URL, bytes: 12_000 },
  
  { url: '/assets/fonts/ZiTiJiaBaXing.ttf', bytes: 5_200_000, label: '核心字体' },
  
  { url: '/assets/start/start-bg-poster.jpg?v=20260507', bytes: 310_000, label: '过场动画' },
  { url: '/assets/start/start-bg.mp4?v=20260507', bytes: 3_760_000, mediaId: 'start-bg' },
  { url: '/assets/start/start-button.webp', bytes: 150_000 },
  { url: '/assets/ui/cursors/game-arrow-red-cursor.png?v=20260507', bytes: 2_000 },
  
  { url: '/assets/gameplay/arena-bg-1440x1080.mp4?v=20260507', bytes: 210_000, label: '图片资源' },
  { url: '/assets/gameplay/arena-bg-poster.png?v=20260507', bytes: 650_000 },
  { url: '/assets/gameplay/paddles/ai-paddle-blue.png?v=20260507', bytes: 40_000 },
  { url: '/assets/gameplay/paddles/player-paddle-red.png?v=20260507', bytes: 50_000 },
  { url: '/assets/ai_icon.png', bytes: 35_000 },
  { url: '/assets/human_icon.png', bytes: 35_000 },
  { url: '/assets/ui/skill-icons-sheet.png?v=20260507', bytes: 220_000 },
  { url: '/assets/ui/game-over-panel.png?v=20260507', bytes: 450_000 },
  { url: '/assets/ui/game-over-button.png?v=20260507', bytes: 230_000 },
  
  // 技能视频：会被转成 Blob URL + 预热到首帧 ready，避免播放时黑屏
  { url: '/assets/skill-cutins/qianying.mp4?v=20260507', bytes: 4_020_000, label: '技能资源', skillId: 'qianying' },
  { url: '/assets/skill-cutins/flame.mp4?v=20260507', bytes: 4_340_000, skillId: 'flame' },
  { url: '/assets/skill-cutins/thunder.mp4?v=20260507', bytes: 4_230_000, skillId: 'thunder' },
  { url: '/assets/skill-cutins/light.mp4?v=20260507', bytes: 3_920_000, skillId: 'light' },
  { url: '/assets/skill-cutins/serpent.mp4?v=20260507', bytes: 4_110_000, skillId: 'serpent' },
  { url: '/assets/skill-cutins/fold.mp4?v=20260507', bytes: 3_960_000, skillId: 'fold' },
  { url: '/assets/skill-cutins/double.mp4?v=20260507', bytes: 3_740_000, skillId: 'double' },
]

// 视频缓存：把下载好的 mp4 转成 Blob URL，并预热一个隐藏的 video 元素到首帧 ready 状态
// - 技能视频：玩家/AI 释放技能时直接复用，避免 1s 黑屏
// - 开始页视频：进入开始页时立即可播，不再 poster -> 卡顿 -> 播放
const skillCutinBlobUrls: Record<string, string> = {}
const mediaBlobUrls: Record<string, string> = {}
const warmedVideos: Record<string, HTMLVideoElement> = {}

function warmupVideo(key: string, blobUrl: string): Promise<HTMLVideoElement> {
  return new Promise((resolve) => {
    const video = document.createElement('video')
    video.preload = 'auto'
    video.muted = true
    video.playsInline = true
    video.src = blobUrl
    video.style.position = 'absolute'
    video.style.width = '1px'
    video.style.height = '1px'
    video.style.opacity = '0'
    video.style.pointerEvents = 'none'
    video.style.left = '-9999px'
    document.body.appendChild(video)

    let resolved = false
    const finish = (): void => {
      if (resolved) return
      resolved = true
      // 强制 seek 一帧，让浏览器解码出第一帧
      try {
        video.currentTime = 0.001
      } catch {
        // ignore
      }
      warmedVideos[key] = video
      resolve(video)
    }

    video.addEventListener('loadeddata', finish, { once: true })
    video.addEventListener('canplay', finish, { once: true })
    video.addEventListener('error', finish, { once: true })
    // 兜底：5秒内如果没回调，也认为加载完成（避免预加载界面卡死）
    window.setTimeout(finish, 5000)
    video.load()
  })
}

declare global {
  interface Window {
    __pingPongPreload?: {
      getSkillCutinUrl: (skillId: string) => string | null
    }
    __pingPongLoadingBoot?: {
      stop: () => void
    }
  }
}

window.__pingPongPreload = {
  getSkillCutinUrl: (skillId: string) => skillCutinBlobUrls[skillId] ?? null,
}

const preloadScreenEl = document.getElementById('preload-screen')
const preloadBarEl = document.getElementById('preload-bar-fill')
const preloadPercentEl = document.getElementById('preload-percent')
const preloadDetailEl = document.getElementById('preload-detail')

const totalBytes = PRELOAD_ASSETS.reduce((sum, a) => sum + a.bytes, 0)
const loadedBytesPerAsset: number[] = PRELOAD_ASSETS.map(() => 0)
let displayedPreloadPercent = Math.max(0, Number.parseInt(preloadPercentEl?.textContent ?? '0', 10) || 0)
let preloadProgressFloor = displayedPreloadPercent
let preloadVisualTicker: number | null = null
let currentPreloadLabel = '资源'
let preloadComplete = false

function formatMegabytes(bytes: number): string {
  return `${(bytes / 1_000_000).toFixed(1)}M`
}

function updatePreloadProgress(): void {
  const loaded = loadedBytesPerAsset.reduce((s, n) => s + n, 0)
  const ratio = Math.max(0, Math.min(loaded / totalBytes, 1))
  const realPercent = Math.floor(ratio * 100)
  const targetPercent = preloadComplete
    ? 100
    : Math.min(96, Math.max(realPercent, Math.floor(preloadProgressFloor)))
  const percent = Math.max(displayedPreloadPercent, targetPercent)

  displayedPreloadPercent = percent
  if (preloadBarEl) preloadBarEl.style.width = `${percent}%`
  if (preloadPercentEl) preloadPercentEl.textContent = `${percent}%`
  if (preloadDetailEl && !preloadComplete) {
    preloadDetailEl.textContent = `正在加载：${currentPreloadLabel} ${formatMegabytes(loaded)} / ${formatMegabytes(totalBytes)}`
  }
}

function setPreloadDetail(text: string): void {
  if (preloadDetailEl) preloadDetailEl.textContent = text
}

function startPreloadVisualTicker(): void {
  const startedAt = performance.now()
  preloadVisualTicker = window.setInterval(() => {
    const elapsedMs = performance.now() - startedAt
    // 这部分表示“建立连接 / 等待首包 / 浏览器调度”的可视化进度。
    // 真实资源字节进度一旦超过它，就由真实进度接管；完成前最高只到 18%，不会假装加载完。
    preloadProgressFloor = Math.min(18, 1 + Math.log1p(elapsedMs / 700) * 4.2)
    updatePreloadProgress()
  }, 180)
}

function stopPreloadVisualTicker(): void {
  if (preloadVisualTicker === null) return
  window.clearInterval(preloadVisualTicker)
  preloadVisualTicker = null
}

async function fetchAssetWithProgress(asset: PreloadAsset, idx: number): Promise<Blob | null> {
  try {
    loadedBytesPerAsset[idx] = Math.max(loadedBytesPerAsset[idx], asset.bytes * 0.01)
    updatePreloadProgress()

    const response = await fetch(asset.url, { cache: 'force-cache' })
    if (!response.ok || !response.body) {
      loadedBytesPerAsset[idx] = asset.bytes
      updatePreloadProgress()
      return null
    }

    loadedBytesPerAsset[idx] = Math.max(loadedBytesPerAsset[idx], asset.bytes * 0.03)
    updatePreloadProgress()

    const lengthHeader = response.headers.get('content-length')
    const totalSize = lengthHeader ? Number.parseInt(lengthHeader, 10) || asset.bytes : asset.bytes
    const reader = response.body.getReader()
    const chunks: Uint8Array[] = []
    let loaded = 0

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      if (value) {
        chunks.push(value)
        loaded += value.length
        loadedBytesPerAsset[idx] = Math.min((loaded / totalSize) * asset.bytes, asset.bytes)
        updatePreloadProgress()
      }
    }

    loadedBytesPerAsset[idx] = asset.bytes
    updatePreloadProgress()
    const blob = new Blob(chunks as BlobPart[], { type: 'video/mp4' })

    // 技能视频：转成 Blob URL 并预热 video 到首帧 ready
    if (asset.skillId) {
      const blobUrl = URL.createObjectURL(blob)
      skillCutinBlobUrls[asset.skillId] = blobUrl
      await warmupVideo(`skill-${asset.skillId}`, blobUrl)
    }

    // 通用视频（如开始页）：转成 Blob URL 并预热到首帧 ready
    if (asset.mediaId) {
      const blobUrl = URL.createObjectURL(blob)
      mediaBlobUrls[asset.mediaId] = blobUrl
      await warmupVideo(`media-${asset.mediaId}`, blobUrl)
    }

    return blob
  } catch {
    loadedBytesPerAsset[idx] = asset.bytes
    updatePreloadProgress()
    return null
  }
}

async function ensureFontReady(): Promise<void> {
  try {
    if (document.fonts && 'load' in document.fonts) {
      await document.fonts.load('1em "ZiTiJiaBaXing"')
      await document.fonts.ready
    }
  } catch {
    // Font load may fail in some browsers, just continue.
  }
}

async function runPreload(): Promise<void> {
  const startTs = performance.now()
  console.log(`[preload] start ${formatMegabytes(totalBytes)} estimated`)
  window.__pingPongLoadingBoot?.stop()
  startPreloadVisualTicker()
  setPreloadDetail(`正在加载资源… 0.0M / ${formatMegabytes(totalBytes)}`)

  const concurrency = 4
  let cursor = 0

  const worker = async (): Promise<void> => {
    while (cursor < PRELOAD_ASSETS.length) {
      const idx = cursor++
      const asset = PRELOAD_ASSETS[idx]
      if (asset.label) currentPreloadLabel = asset.label
      await fetchAssetWithProgress(asset, idx)
    }
  }

  await Promise.all(Array.from({ length: concurrency }, worker))
  setPreloadDetail('正在校准字体与视频…')
  await ensureFontReady()

  setPreloadDetail('准备就绪')
  stopPreloadVisualTicker()
  preloadComplete = true
  // 全量关键资源到位后，让进度条最终定格在 100%
  loadedBytesPerAsset.forEach((_, i) => (loadedBytesPerAsset[i] = PRELOAD_ASSETS[i].bytes))
  updatePreloadProgress()

  const elapsedMs = Math.round(performance.now() - startTs)
  console.log(`[preload] done ${elapsedMs}ms`)
  window.dispatchEvent(new CustomEvent('preload:done', { detail: { ms: elapsedMs } }))
}

function hidePreloadScreen(): void {
  if (!preloadScreenEl) return
  preloadScreenEl.classList.add('is-hidden')
  window.setTimeout(() => preloadScreenEl?.remove(), 600)
}

/** 等待 video 元素的第一帧解码完成（loadeddata / canplay 之一）。最多等 timeoutMs 兜底。*/
function waitVideoFrameReady(video: HTMLVideoElement, timeoutMs = 3000): Promise<void> {
  if (video.readyState >= 2 /* HAVE_CURRENT_DATA */) return Promise.resolve()
  return new Promise((resolve) => {
    let done = false
    const finish = (): void => {
      if (done) return
      done = true
      video.removeEventListener('loadeddata', finish)
      video.removeEventListener('canplay', finish)
      video.removeEventListener('error', finish)
      resolve()
    }
    video.addEventListener('loadeddata', finish, { once: true })
    video.addEventListener('canplay', finish, { once: true })
    video.addEventListener('error', finish, { once: true })
    window.setTimeout(finish, timeoutMs)
  })
}

/** 尝试播放 video，失败时短暂等待后重试，最多 maxAttempts 次 */
async function tryPlayVideoWithRetry(video: HTMLVideoElement, maxAttempts = 3): Promise<boolean> {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      await video.play()
      return true
    } catch {
      if (attempt < maxAttempts) {
        await new Promise((r) => setTimeout(r, 120))
      }
    }
  }
  return false
}

/** 把开始页 video 切到预热好的 Blob URL，并保证首帧已经准备好。失败时返回 false 让外层兜底。*/
async function primeStartVideo(): Promise<boolean> {
  if (!startVideo) return false

  const cachedUrl = mediaBlobUrls['start-bg']
  if (cachedUrl && startVideo.src !== cachedUrl) {
    startVideo.src = cachedUrl
  } else if (!cachedUrl && !startVideo.src) {
    // 兜底：缓存没准备好时回退到远程 URL
    startVideo.src = '/assets/start/start-bg.mp4?v=20260507'
  }
  startVideo.muted = true
  startVideo.loop = true
  startVideo.playsInline = true
  startVideo.preload = 'auto'
  try {
    startVideo.load()
    startVideo.currentTime = 0
  } catch {
    // ignore
  }

  await waitVideoFrameReady(startVideo)

  return tryPlayVideoWithRetry(startVideo)
}

/** 当 startVideo 未能自动播放时，第一次用户交互时立即重试播放。 */
function ensureStartVideoOnFirstInteraction(): void {
  if (!startVideo) return

  const tryRecover = async (): Promise<void> => {
    if (!startVideo || !startVideo.paused) {
      cleanup()
      return
    }
    const ok = await tryPlayVideoWithRetry(startVideo, 2)
    if (ok) cleanup()
  }

  const cleanup = (): void => {
    window.removeEventListener('pointerdown', tryRecover, true)
    window.removeEventListener('pointerup', tryRecover, true)
    window.removeEventListener('click', tryRecover, true)
    window.removeEventListener('keydown', tryRecover, true)
    window.removeEventListener('touchstart', tryRecover, true)
  }

  window.addEventListener('pointerdown', tryRecover, true)
  window.addEventListener('pointerup', tryRecover, true)
  window.addEventListener('click', tryRecover, true)
  window.addEventListener('keydown', tryRecover, true)
  window.addEventListener('touchstart', tryRecover, true)
}

function bootStartScreen(): void {
  initPingPongSidePanel()

  // 创建游戏实例
  new Phaser.Game(config)

  // 进入开始页时立刻尝试播放 BGM。如果用户在 Loading 阶段已经点击过任意位置，
  // 此时 audio 已经解锁；如果还没点击过，会被浏览器拦截，等下次用户交互时
  // unlockStartAudio 监听器会自动重试。
  void playStartBgm()
  window.addEventListener('ping-pong:skill-cutin-start', duckGameBgm)
  window.addEventListener('ping-pong:skill-cutin-end', restoreGameBgm)
  window.addEventListener('ping-pong:return-hit', duckGameBgmForReturnHit)
  window.addEventListener('ping-pong:replay-click', () => {
    playStartButtonSfx()
    playGameBgm()
  })

  startButton?.addEventListener('click', () => {
    if (!startAudioUnlocked) {
      void playStartBgm()
    }

    playStartButtonSfx()

    if (arenaReady) {
      enterGame()
      return
    }

    waitingForArena = true
    startButton.classList.add('is-loading')
    startButton.setAttribute('aria-busy', 'true')
    window.dispatchEvent(new CustomEvent('ping-pong:prepare-arena'))
  })
}

void runPreload().then(async () => {
  const startVideoPlayed = await primeStartVideo()
  bootStartScreen()
  hidePreloadScreen()

  // 兜底：如果首次自动播放被浏览器拦截，注册一个一次性监听器，
  // 等用户首次任意交互时立即重试播放，确保不会停在 poster 静态图。
  if (!startVideoPlayed) {
    ensureStartVideoOnFirstInteraction()
  }
})
